
// CourseAdminPanel.jsx
import React, { useState, useEffect } from "react";
import "./adminEnroll.css";

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL+"/api"; // Backend URL

// API Functions
const fetchEnrollments = async () => {
  const response = await fetch(`${API_BASE_URL}/enrollments`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

const deleteEnrollment = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/enrollments/${id}`, {
      method: "DELETE",
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (err) {
    console.error("Delete API Error:", err.message);
    throw err;
  }
};


// Icons
const TrashIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3,6 5,6 21,6"></polyline>
    <path d="m19,6v14a2,2 0,0 1,-2,2H7a2,2 0,0 1,-2,-2V6m3,0V4a2,2 0,0 1,2 -2h4a2,2 0,0 1,2,2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const AlertIcon = () => (
  <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
  </svg>
);

const DocumentIcon = () => (
  <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
  </svg>
);

const CourseAdminPanel = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchEnrollments();
      setEnrollments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to fetch enrollments");
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this enrollment?")) return;
    try {
      setDeleting(id);
      setError(null);
      await deleteEnrollment(id);
      setEnrollments((prev) => prev.filter((e) => e._id !== id));
    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
      loadEnrollments();
    } finally {
      setDeleting(null);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    await loadEnrollments();
    setRetrying(false);
  };

  // Error UI
  if (error && !loading) {
    return (
      <div className="admin-container">
        <div className="admin-wrapper">
          <div className="admin-header">
            <h1 className="admin-title">Admin Panel</h1>
          </div>
          <div className="admin-content">
            <h2 className="content-title">Course Register Details</h2>
            <div className="error-container">
              <AlertIcon />
              <p className="error-title">Error Loading Data</p>
              <p className="error-message">{error}</p>
              <button onClick={handleRetry} disabled={retrying} className="retry-btn">
                {retrying ? "Retrying..." : "Retry"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading UI
  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-wrapper">
          <div className="admin-header">
            <h1 className="admin-title">Admin Panel</h1>
          </div>
          <div className="admin-content">
            <h2 className="content-title">Course Register Details</h2>
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <span>Loading enrollments...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main UI
  return (
    <div className="admin-container">
      <div className="admin-wrapper">
        <div className="admin-header">
          <h1 className="admin-title">Admin Panel</h1>
        </div>

        <div className="admin-content">
          <div className="content-header">
            <h2 className="content-title">Course Register Details</h2>
            <button onClick={loadEnrollments} disabled={loading} className="refresh-btn">
              Refresh Data
            </button>
          </div>

          {enrollments.length === 0 ? (
            <div className="empty-state">
              <DocumentIcon />
              <p className="empty-title">No enrollments found</p>
              <p className="empty-subtitle">Try refreshing or check your database</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead className="table-header">
                  <tr>
                    <th>S.NO</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Course</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((enroll, index) => (
                    <tr key={enroll._id} className="table-row">
                      <td className="table-cell">{index + 1}</td>
                      <td className="table-cell">{enroll.name}</td>
                      <td className="table-cell">{enroll.email}</td>
                      <td className="table-cell">{enroll.phone}</td>
                      <td className="table-cell">{enroll.address}</td>
                      <td className="table-cell">{enroll.course}</td>
                      <td className="table-cell text-center">
                        <button
                          onClick={() => handleDelete(enroll._id)}
                          disabled={deleting === enroll._id}
                          className="delete-btn"
                          title="Delete enrollment"
                        >
                          {deleting === enroll._id ? "Deleting..." : <TrashIcon size={20} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {enrollments.length > 0 && (
            <div className="stats">Total enrollments: {enrollments.length}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseAdminPanel;


